<script>
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import Icon from "@/javascript/views/components/Icon.vue";
import LoadingSpinner from "../components/loadingSpinner.vue";
import Popup from "../components/popup.vue";
import {useUserStore} from "../../stores/user";
import {useOrganizationStore} from "../../stores/organizations";
import OrganizationCard from "../components/overview/OrganizationCard.vue";
import AccordionItem from "../components/accordion/AccordionItem.vue";
import DropdownSelect from "../components/dropdownSelect.vue";


/**
 * This is the intermediate route when the user first logs in
 * It checks for permissions before routing to the appropriate dashboard (client, donor dash)
 * It also checks for TOS, etc and forces the user to agree before proceeding any further
 */

export default {
  name: 'OrganizationSelector',
  components: {DropdownSelect, AccordionItem, OrganizationCard, Icon, LoadingSpinner, Popup},

  emits: ['organization-selected', 'close'],
  setup() {
    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();

    return {
      router,
      route,
      userStore,
      organizationStore
    };
  },

  data() {
    return {
      organizations: [],
      filteredOrganizations: [],
      selectedOrganization: null,
      sortOptions: ['name', 'other'],
      sortValue: 'name',
      searchQuery: "",
      popupMessage: ""
    };
  },

  computed: {},


  methods: {
    onClick(org) {
      this.selectedOrganization = org;
    },
    switchOrganization() {
      this.$emit('organization-selected', this.selectedOrganization);
    },

    setAsDefault() {
      this.userStore.setDefaultOrganization(this.selectedOrganization.id).then(() => {
        this.popupMessage = `${this.selectedOrganization.name} set as default`;
        this.$refs.popup.show();
      }).catch(() => {
        this.popupMessage = "Failed to set default organization";
        this.$refs.errorPopup.show();
      });
    },

    // Formats, sorts and filters all orgs in the list
    formattedOrganizations() {
      let orgs = this.organizeHierarchy();
      orgs = this.filterOrganizations(orgs);
      return this.sortOrganizations(orgs);
    },

    clearSearch() {
      this.searchQuery = "";
      this.formattedOrganizations();
    },

    // Filters the local organizations by search query.
    // Includes orgs whose descendants also match the query
    filterOrganizations(organizations) {
      const searchLower = this.searchQuery.toLowerCase();
      const results = [];

      for (const org of organizations) {
        const matches = org.name.toLowerCase().includes(searchLower);
        let filteredChildren = [];

        if (org.children && Array.isArray(org.children)) {
          for (const child of org.children) {
            const childMatches = child.name.toLowerCase().includes(searchLower);
            let filteredGrandChildren = [];

            if (child.children && Array.isArray(child.children)) {
              filteredGrandChildren = child.children.filter(grandChild =>
                grandChild.name.toLowerCase().includes(searchLower)
              );
            }

            if (childMatches || filteredGrandChildren.length > 0) {
              filteredChildren.push({
                ...child,
                children: filteredGrandChildren
              });
            }
          }
        }

        if (matches || filteredChildren.length > 0) {
          results.push({
            ...org,
            children: filteredChildren
          });
        }
      }

      return results;
    },

    sortOrganizations(orgs) {
      return orgs.sort((a, b) => a.name < b.name ? -1 : 1);
    },

    // This function will organize all the orgs the user has access to in a hierarchy tree
    // To allow for the nested accordions
    organizeHierarchy() {
      const organizations = this.userStore.organizations;
      if (!organizations) return [];
      // Create a map for quick lookup by ID
      const orgMap = new Map();

      // First pass: Create a map of all organizations and initialize children arrays
      organizations.forEach(org => {
        orgMap.set(org.id, {
          ...org,
          children: [] // Add children array to each organization
        });
      });

      // Second pass: Build the hierarchy
      const rootLevel = [];

      organizations.forEach(org => {
        const currentOrg = orgMap.get(org.id);

        // Find if this organization has a parent in our dataset
        let hasParentInDataset = false;

        // Check all organizations to see if any have this org as a child
        for (const potentialParent of organizations) {
          if (potentialParent.child_ids && potentialParent.child_ids.includes(org.id)) {
            // This org has a parent in our dataset
            const parentOrg = orgMap.get(potentialParent.id);
            if (parentOrg) {
              parentOrg.children.push(currentOrg);
              hasParentInDataset = true;
              break;
            }
          }
        }

        // If no parent found in dataset, add to root level
        if (!hasParentInDataset) {
          rootLevel.push(currentOrg);
        }
      });

      // Third pass: Organize children within each parent based on child_ids order
      orgMap.forEach(org => {
        if (org.child_ids && org.child_ids.length > 0) {
          // Sort children based on the order they appear in child_ids
          org.children.sort((a, b) => {
            const indexA = org.child_ids.indexOf(a.id);
            const indexB = org.child_ids.indexOf(b.id);
            return indexA - indexB;
          });
        }
      });

      // Sort root level by depth, then by ID for consistency
      rootLevel.sort((a, b) => {
        if (a.depth !== b.depth) {
          return a.depth - b.depth;
        }
        return a.id - b.id;
      });

      return rootLevel;
    }
  },

};
</script>


<template>
  <div class="page-container">
    <div class="content-container">
      <div class="mt-2 sm:mt-8 w-full flex flex-col sm:flex-row justify-between gap-2">
        <div class="subtitle-medium">
          Organizations
        </div>
        <button
          class="btn btn-outline text-sm px-2 py-1 sm:px-4 sm:py-2"
          @click="$emit('close')"
        >
          Back to {{ userStore.currentOrganization?.name }}
        </button>
      </div>
      <div class="header-container">
        <div class="relative w-full">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search..."
            class="searchbar"
            @input="formattedOrganizations"
          >
          <div class="absolute inset-y-0 right-0 flex items-center pr-3">
            <icon
              v-if="searchQuery"
              icon="x"
              class="cursor-pointer"
              @click="clearSearch"
            />
            <icon
              v-else
              icon="search"
            />
          </div>
        </div>
      </div>
      <div class="organization-list-container">
        <div class="organization-cards-container clean-scrollbar">
          <div
            v-for="organization in formattedOrganizations()"
            :key="userStore.organizations?.length"
            class="organization-card-tree-container"
          >
            <accordion-item
              full-width
              no-border
              no-padding
              no-chevron
              start-open
              @click="onClick(organization)"
            >
              <template #titleSlot>
                <organization-card
                  :organization="organization"
                  :is-default="userStore.user.default_organization_id === organization.id"
                  class="organization-card-container org-level-1"
                  :class="{'selected': selectedOrganization?.uuid === organization.uuid}"
                />
              </template>
              <template #bodySlot>
                <accordion-item
                  v-for="child in organization.children"
                  full-width
                  no-padding
                  no-border
                  no-chevron
                  start-open
                  @click.stop="onClick(child)"
                >
                  <template #titleSlot>
                    <organization-card
                      :organization="child"
                      :is-default="userStore.user.default_organization_id === child.id"
                      label-colour="#00bdff"
                      class="organization-card-container org-level-2"
                      :class="{'selected': selectedOrganization?.uuid === child.uuid}"
                    />
                  </template>
                  <template #bodySlot>
                    <organization-card
                      v-for="grandChild in child.children"
                      :organization="grandChild"
                      :is-default="userStore.user.default_organization_id === grandChild.id"
                      label-colour="#08602d"
                      class="organization-card-container org-level-3"
                      :class="{'selected': selectedOrganization?.uuid === grandChild.uuid}"
                      @click.stop="onClick(grandChild)"
                    />
                  </template>
                </accordion-item>
              </template>
            </accordion-item>
          </div>
        </div>

        <div
          v-if="selectedOrganization"
          class="organization-details-container border sm:border-0 border-gray-300 mt-4 sm:mt-0"
        >
          <!-- DETAILS HEADER -->
          <div class="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-4">
            <div
              class="relative w-8 h-8 sm:w-16 sm:h-16 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center"
            >
              <div class="absolute left-0 top-0 bottom-0 w-1 sm:w-2 rounded-l-lg bg-default_secondary" />
              <div class="text-sm sm:text-2xl font-semibold text-gray-500">
                {{
                  selectedOrganization.name.charAt(0)
                }}
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-lg font-semibold truncate">
                {{ selectedOrganization.name }}
              </h3>
            </div>
            <div class="flex-shrink-0 flex flex-col sm:flex-row gap-1 sm:gap-6">
              <div
                v-if="selectedOrganization.descendant_count"
                class="flex items-center gap-1"
              >
                <icon
                  icon="sitemap"
                  size="16px"
                  color="gray"
                />
                <div class="text-xs sm:text-base">
                  {{ selectedOrganization.descendant_count || 0 }}
                </div>
              </div>
              <div class="flex items-center gap-1">
                <icon
                  icon="users"
                  size="16px"
                  color="gray"
                />
                <div class="text-xs sm:text-base">
                  {{ selectedOrganization.member_count || 0 }}
                </div>
              </div>
            </div>
          </div>

          <div class="divider" />

          <div class="organization-details-row">
            <div>
              <label class="label">
                <icon
                  icon="address"
                  size="14px"
                />
                <span>Address</span>
              </label>
              <span class="value">{{ selectedOrganization.address }}</span>
            </div>
            <div>
              <label class="label">
                <icon
                  icon="city"
                  size="14px"
                />
                <span>City</span>
              </label>
              <span class="value">{{ selectedOrganization.city }}</span>
            </div>
            <div>
              <label class="label">
                <icon
                  icon="globe"
                  size="14px"
                />
                <span>State</span>
              </label>
              <span class="value">{{ selectedOrganization.state }}</span>
            </div>
          </div>

          <div class="divider" />


          <div class="organization-details-row">
            <div>
              <label class="label">
                <icon
                  icon="envelope"
                  size="14px"
                />
                <span>Email</span>
              </label>
              <span class="value">{{ selectedOrganization.email }}</span>
            </div>
            <div>
              <label class="label">
                <icon
                  icon="link-solid"
                  size="14px"
                />
                <span>Website</span>
              </label>
              <span class="value">
                <a
                  target="_blank"
                  :href="selectedOrganization.website_url"
                  class="hover:text-blue-700"
                >
                  {{ selectedOrganization.website_url }}
                </a>
              </span>
            </div>
            <div>
              <label class="label">
                <icon
                  icon="phone"
                  size="14px"
                />
                <span>Phone Number</span>
              </label>
              <span class="value">{{ selectedOrganization.phone_number }}</span>
            </div>
          </div>

          <div class="divider" />

          <div class="flex justify-end gap-3">
            <button
              v-if="selectedOrganization && userStore.user.default_organization_id !== selectedOrganization.id"
              class="btn btn-outline text-sm"
              @click="setAsDefault"
            >
              Set as Default
            </button>
            <button
              class="btn button-filled"
              @click="switchOrganization"
            >
              Switch to Organization View
            </button>
          </div>
        </div>
      </div>
    </div>
    <popup
      ref="popup"
      :message="popupMessage"
      :duration="1400"
    />
    <popup
      ref="errorPopup"
      :message="popupMessage"
      :duration="2500"
      type="error"
    />
  </div>
</template>


<style scoped lang="scss">

.page-container {
  @apply flex flex-col h-screen overflow-hidden p-2;
  @apply bg-gray-100;

  .content-container {
    @apply flex flex-col h-full;
    @apply bg-white border-2 p-2;

    @screen sm {
      @apply rounded-md;
    }
    @screen md {
      @apply p-4;
    }
  }

  .header-container {
    @apply w-full flex flex-row;
    @apply min-h-12 sm:min-h-16 items-center;
    border-bottom-width: 1px;
    border-color: lightgrey;
  }
}

.organization-list-container {
  @apply w-full h-full overflow-hidden flex flex-col sm:flex-row;
}

.organization-cards-container {
  @apply pt-2 sm:pt-4 pr-2 sm:pr-8;
  @apply flex flex-col bg-white w-full sm:w-1/2 overflow-x-auto;
  border-right-width: 0;
  border-color: lightgrey;

  @screen sm {
    border-right-width: 1px;
  }
}

.organization-card-container {
  @apply cursor-pointer rounded-lg text-gray-900;

  &.org-level-1 {
    .organization-card-tree-container:nth-child(even) & {
      @apply bg-table_gray;
    }
  }

  &.org-level-2 {
    @apply pl-6 sm:pl-12;
    .accordion-item:nth-child(odd) & {
      @apply bg-table_gray;
    }
  }

  &.org-level-3 {
    @apply pl-10 sm:pl-20;

    &:nth-child(even) {
      @apply bg-table_gray;
    }
  }

  &.selected {
    background-color: #10be5a !important;
    color: white !important;

    :deep(.icon) svg path {
      fill: white;
    }
  }

  &:hover {
    background-color: #10be5a !important;
    color: white !important;

    :deep(.icon) svg path {
      fill: white;
    }
  }
}

.organization-details-container {
  @apply w-full sm:w-1/2;
  @apply mx-auto bg-white rounded-lg p-2 sm:p-8;

  .organization-details-row {
    @apply grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-8 mb-3 sm:mb-6;

    .label {
      @apply flex gap-1 text-xs font-medium text-gray-400 uppercase tracking-wide mb-1;
      :deep(.icon) svg path {
        @apply fill-gray-400;
      }
    }

    .value {
      @apply text-xs sm:text-sm font-medium text-gray-900 break-words;
    }
  }
}

.searchbar {
  @apply w-full h-8 sm:h-10 px-3 sm:px-4 py-1 sm:py-2 pr-8 sm:pr-10 text-sm sm:text-base text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent;
}

.divider {
  @apply border-t border-gray-200 mb-3 sm:mb-6;
}

:deep(.dropdown-select) {
  @apply bg-white border-gray-200 rounded-md;
  border-width: 2px;

}

</style>
